import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useForm } from 'react-hook-form';
import { api, unwrap } from '../../../api/client';
import { errorMessage } from '../../../api/errors';
import type { components } from '../../../api/schema';
import { useAuth } from '../../../auth/AuthProvider';
import { Button, Card, Field, Input } from '../../../design';
import { useToast } from '../../../design/Toast';
import type { LockerDetail } from '../common';
import s from '../lockers.module.css';

type Rules = components['schemas']['LockerRules'];

export function RulesTab({ locker: l }: { locker: LockerDetail }) {
  const { has } = useAuth();
  const canEdit = has('configure');
  const qc = useQueryClient();
  const toast = useToast();
  const form = useForm<Rules>({ defaultValues: l.rules });
  const save = useMutation({
    mutationFn: (r: Rules) =>
      unwrap(
        api.PUT('/lockers/{id}/rules', {
          params: { path: { id: l.id } },
          body: {
            keyExpiryHours: Number(r.keyExpiryHours),
            reminderHours: Number(r.reminderHours),
            doorTimeoutSec: Number(r.doorTimeoutSec),
            fillAlertPct: Number(r.fillAlertPct),
          },
        }),
      ),
    onSuccess: () => {
      toast('Regulile au fost salvate; versiunea configurației a crescut.', 'success');
      void qc.invalidateQueries({ queryKey: ['locker', l.id] });
    },
    onError: (e) => toast(errorMessage(e), 'error'),
  });
  const errors = form.formState.errors;
  return (
    <Card style={{ maxWidth: 720 }}>
      <h2>Acces & reguli locale</h2>
      <p className="muted small">
        Se aplică operațiunilor noi ale acestui locker; politicile globale sunt în Setări &
        securitate.
      </p>
      <form
        className="stack"
        style={{ marginTop: 12 }}
        onSubmit={form.handleSubmit((r) => save.mutate(r))}
      >
        <div className={s.formGrid}>
          <Field
            label="Valabilitatea cheilor (ore)"
            hint="1–168"
            error={errors.keyExpiryHours?.message}
            htmlFor="r-exp"
          >
            <Input
              id="r-exp"
              type="number"
              min={1}
              max={168}
              disabled={!canEdit}
              invalid={!!errors.keyExpiryHours}
              {...form.register('keyExpiryHours', {
                required: true,
                min: { value: 1, message: 'Minimum 1' },
                max: { value: 168, message: 'Maximum 168' },
              })}
            />
          </Field>
          <Field
            label="Reamintire înainte de expirare (ore)"
            hint="Mai mic decât valabilitatea"
            error={errors.reminderHours?.message}
            htmlFor="r-rem"
          >
            <Input
              id="r-rem"
              type="number"
              min={1}
              max={167}
              disabled={!canEdit}
              invalid={!!errors.reminderHours}
              {...form.register('reminderHours', {
                required: true,
                validate: (v, all) =>
                  Number(v) < Number(all.keyExpiryHours) ||
                  'Trebuie să fie mai mică decât valabilitatea',
              })}
            />
          </Field>
          <Field
            label="Prag ușă deschisă (secunde)"
            hint="15–600"
            error={errors.doorTimeoutSec?.message}
            htmlFor="r-door"
          >
            <Input
              id="r-door"
              type="number"
              min={15}
              max={600}
              disabled={!canEdit}
              invalid={!!errors.doorTimeoutSec}
              {...form.register('doorTimeoutSec', {
                required: true,
                min: { value: 15, message: 'Minimum 15' },
                max: { value: 600, message: 'Maximum 600' },
              })}
            />
          </Field>
          <Field
            label="Alertă de ocupare (%)"
            hint="1–100"
            error={errors.fillAlertPct?.message}
            htmlFor="r-fill"
          >
            <Input
              id="r-fill"
              type="number"
              min={1}
              max={100}
              disabled={!canEdit}
              invalid={!!errors.fillAlertPct}
              {...form.register('fillAlertPct', {
                required: true,
                min: { value: 1, message: 'Minimum 1' },
                max: { value: 100, message: 'Maximum 100' },
              })}
            />
          </Field>
        </div>
        {canEdit && (
          <div>
            <Button variant="primary" type="submit" busy={save.isPending}>
              Salvează regulile
            </Button>
          </div>
        )}
      </form>
    </Card>
  );
}
